import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { API, SERVICES } from "../lib/api";

const PROJECT_SIZES = ["Small (<5k sqft)", "Medium (5k-25k sqft)", "Large (25k+ sqft)", "Enterprise / Multi-site"];
const TIMELINES = ["ASAP", "1-3 months", "3-6 months", "6+ months", "Just exploring"];
const BUDGETS = ["<$10k", "$10k-$25k", "$25k-$75k", "$75k-$200k", "$200k+", "Not sure"];

export default function QuotePage() {
  const nav = useNavigate();
  const [form, setForm] = useState({
    name: "", email: "", phone: "", company: "",
    services: [], project_size: "", timeline: "", budget: "",
    site_address: "", details: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const upd = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const toggleService = (name) => {
    setForm((s) => ({
      ...s,
      services: s.services.includes(name)
        ? s.services.filter((x) => x !== name)
        : [...s.services, name],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.services.length === 0) {
      toast.error("Please select at least one service.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/leads/quote`, form);
      toast.success(res.data.message || "Quote request received.");
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail?.[0]?.msg || "Submission failed. Please check the form.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-24 text-center" data-testid="quote-success">
        <CheckCircle2 size={56} className="text-[#1E3A6B] mx-auto" />
        <h1 className="font-display font-bold text-3xl mt-6 text-slate-900">Quote request received.</h1>
        <p className="mt-3 text-slate-600">A senior engineer will reach out within one business day.</p>
        <div className="mt-8 flex justify-center gap-3">
          <Button onClick={() => nav("/")} className="rounded-none bg-slate-900 hover:bg-slate-800">Back to home</Button>
          <Button onClick={() => { setDone(false); setForm({ name: "", email: "", phone: "", company: "", services: [], project_size: "", timeline: "", budget: "", site_address: "", details: "" }); }} variant="outline" className="rounded-none">Submit another</Button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="quote-page" className="bg-[#FAFAFA]">
      <section className="border-b border-slate-200 bg-white">
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-14">
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#1E3A6B] mb-3">— Get Started</div>
          <h1 className="font-display font-black tracking-tight text-4xl lg:text-5xl text-slate-900">
            Request a quote.
          </h1>
          <p className="mt-4 text-slate-600 leading-relaxed max-w-2xl">
            The more you tell us, the more accurate your estimate. A senior engineer
            reviews every submission within 24 business hours.
          </p>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 md:px-8 py-12">
        <form onSubmit={submit} className="space-y-10 bg-white border border-slate-200 p-6 md:p-10" data-testid="quote-form">
          <div>
            <h2 className="font-display font-bold text-xl text-slate-900 mb-1">Your details</h2>
            <p className="text-sm text-slate-500 mb-6">We'll only use this to respond to your inquiry.</p>
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Full name *" id="name"><Input data-testid="quote-input-name" id="name" required value={form.name} onChange={(e) => upd("name", e.target.value)} /></Field>
              <Field label="Company" id="company"><Input data-testid="quote-input-company" id="company" value={form.company} onChange={(e) => upd("company", e.target.value)} /></Field>
              <Field label="Email *" id="email"><Input data-testid="quote-input-email" id="email" type="email" required value={form.email} onChange={(e) => upd("email", e.target.value)} /></Field>
              <Field label="Phone *" id="phone"><Input data-testid="quote-input-phone" id="phone" required value={form.phone} onChange={(e) => upd("phone", e.target.value)} /></Field>
            </div>
          </div>

          <div>
            <h2 className="font-display font-bold text-xl text-slate-900 mb-1">Services needed *</h2>
            <p className="text-sm text-slate-500 mb-6">Select all that apply.</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {SERVICES.map((s) => (
                <label
                  key={s.slug}
                  data-testid={`quote-service-${s.slug}`}
                  className={`border p-4 cursor-pointer flex items-start gap-3 transition-colors ${
                    form.services.includes(s.name) ? "border-[#1E3A6B] bg-[#EEF2F8]" : "border-slate-300 hover:border-slate-500"
                  }`}
                >
                  <Checkbox
                    checked={form.services.includes(s.name)}
                    onCheckedChange={() => toggleService(s.name)}
                    className="mt-0.5"
                  />
                  <div>
                    <div className="font-display font-semibold text-slate-900">{s.name}</div>
                    <div className="text-xs text-slate-600 mt-1">{s.short}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div>
            <h2 className="font-display font-bold text-xl text-slate-900 mb-1">Project parameters</h2>
            <div className="grid sm:grid-cols-3 gap-5 mt-6">
              <Field label="Project size *" id="project_size">
                <SelectField testid="quote-select-size" value={form.project_size} onChange={(v) => upd("project_size", v)} options={PROJECT_SIZES} placeholder="Select size" />
              </Field>
              <Field label="Timeline *" id="timeline">
                <SelectField testid="quote-select-timeline" value={form.timeline} onChange={(v) => upd("timeline", v)} options={TIMELINES} placeholder="Select timeline" />
              </Field>
              <Field label="Budget *" id="budget">
                <SelectField testid="quote-select-budget" value={form.budget} onChange={(v) => upd("budget", v)} options={BUDGETS} placeholder="Select budget" />
              </Field>
            </div>
          </div>

          <div>
            <h2 className="font-display font-bold text-xl text-slate-900 mb-1">Site & scope</h2>
            <div className="space-y-5 mt-6">
              <Field label="Site address *" id="site_address">
                <Input data-testid="quote-input-address" id="site_address" required value={form.site_address} onChange={(e) => upd("site_address", e.target.value)} placeholder="Street, City, State" />
              </Field>
              <Field label="Project details *" id="details">
                <Textarea data-testid="quote-input-details" id="details" required rows={6} value={form.details} onChange={(e) => upd("details", e.target.value)} placeholder="Buildings, doors, drops, room counts, existing equipment, special requirements…" />
              </Field>
            </div>
          </div>

          <div className="flex items-center justify-between border-t border-slate-200 pt-6">
            <p className="text-sm text-slate-500">We typically respond within one business day.</p>
            <Button data-testid="quote-submit-btn" type="submit" disabled={loading} className="rounded-none bg-[#1E3A6B] hover:bg-[#172F58] h-12 px-8 text-base">
              {loading ? <><Loader2 size={16} className="animate-spin mr-2" />Submitting…</> : "Submit Request"}
            </Button>
          </div>
        </form>
      </section>
    </div>
  );
}

function Field({ label, id, children }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-xs font-mono uppercase tracking-wider text-slate-700">{label}</Label>
      {children}
    </div>
  );
}

function SelectField({ value, onChange, options, placeholder, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="rounded-none border-slate-300">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}
